import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";

import React, { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import HeadingLayout from "src/components/base/heading-layout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { EllipsisVerticalIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import { PrimaryButton } from "src/components/base/button";
import { ApiInternal } from "src/api/api_internal";
import { useForm } from "@tanstack/react-form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import Form from "src/components/base/form";
import WS from "src/api/ws";
import TablePagination from "src/components/table-pagination";
import CreateDeviceGroupDialog from "src/components/dialogs/create-device-group";
import RenameDeviceGroupDialog from "src/components/dialogs/rename-device-group";
import ChangeDeviceGroupDescDialog from "src/components/dialogs/change-device-group-desc";
import DeleteDeviceGroupDialog from "src/components/dialogs/delete-device-group";
import { SimpleDeviceGroup } from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link DeviceGroupManagement}
 */
export type DeviceGroupManagementProps = {};

const LIMIT = 25;

/**
 * The management overview for device groups
 */
function DeviceGroupManagement(props: DeviceGroupManagementProps) {
    const [t] = useTranslation("admin-device-group-overview");
    const [tg] = useTranslation();

    const navigate = useNavigate();
    const router = useRouter();

    const { page, search } = Route.useSearch();
    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.DeviceGroupsChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    const [openCreateDeviceGroup, setOpenCreateDeviceGroup] = React.useState(false);
    const [openRenameDeviceGroup, setOpenRenameDeviceGroup] = React.useState<SimpleDeviceGroup>();
    const [openSetDeviceGroupDesc, setOpenSetDeviceGroupDesc] = React.useState<SimpleDeviceGroup>();
    const [openDeleteDeviceGroup, setOpenDeleteDeviceGroup] = React.useState<SimpleDeviceGroup>();

    return (
        <HeadingLayout
            heading={t("heading.device-group-overview")}
            headingDescription={t("description.device-groups")}
            headingChildren={
                <PrimaryButton onClick={() => setOpenCreateDeviceGroup(true)}>
                    {t("button.create-device-group")}
                </PrimaryButton>
            }
        >
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) =>
                                    await navigate({
                                        to: "/a/devicegroups",
                                        search: {
                                            page: 1,
                                            search: fieldApi.state.value,
                                        },
                                    }),
                                onChangeAsyncDebounceMs: 500,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Input
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
            </Form>

            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.device-group-name")}</TableHeader>
                        <TableHeader>{t("label.device-group-description")}</TableHeader>
                        <TableHeader className={"w-0"}>
                            <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((deviceGroup) => (
                        <TableRow
                            key={deviceGroup.uuid}
                            href={"/a/devicegroups/$deviceGroupId/associated"}
                            params={{ deviceGroupId: deviceGroup.uuid }}
                        >
                            <TableCell>{deviceGroup.name}</TableCell>
                            <TableCell className={"w-[50ch] text-wrap break-all whitespace-pre-wrap"}>
                                {deviceGroup.description}
                            </TableCell>
                            <TableCell>
                                <Dropdown>
                                    <DropdownButton plain={true}>
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownSection>
                                            <DropdownItem onClick={() => setOpenRenameDeviceGroup(deviceGroup)}>
                                                <PencilSquareIcon />
                                                <DropdownLabel>{t("button.rename-device-group")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem onClick={() => setOpenSetDeviceGroupDesc(deviceGroup)}>
                                                <PencilSquareIcon />
                                                <DropdownLabel>
                                                    {t("button.set-device-group-description")}
                                                </DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                        <DropdownSection>
                                            <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>
                                            <DropdownItem onClick={() => setOpenDeleteDeviceGroup(deviceGroup)}>
                                                <TrashIcon />
                                                <DropdownLabel>{t("button.delete-device-group")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                    </DropdownMenu>
                                </Dropdown>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/a/devicegroups"}
                getSearchParams={(newPage: number) => ({ search, page: newPage })}
            />

            <Suspense>
                <CreateDeviceGroupDialog
                    isOpen={openCreateDeviceGroup}
                    onClose={() => {
                        setOpenCreateDeviceGroup(false);
                    }}
                />

                <RenameDeviceGroupDialog
                    deviceGroup={openRenameDeviceGroup}
                    isOpen={!!openRenameDeviceGroup}
                    onClose={() => {
                        setOpenRenameDeviceGroup(undefined);
                    }}
                    onSubmit={async () => {
                        setOpenRenameDeviceGroup(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />

                <ChangeDeviceGroupDescDialog
                    deviceGroup={openSetDeviceGroupDesc}
                    isOpen={!!openSetDeviceGroupDesc}
                    onClose={() => {
                        setOpenSetDeviceGroupDesc(undefined);
                    }}
                    onSubmit={async () => {
                        setOpenSetDeviceGroupDesc(undefined);
                        await router.invalidate({ sync: true });
                    }}
                />

                <DeleteDeviceGroupDialog
                    deviceGroup={openDeleteDeviceGroup}
                    isOpen={!!openDeleteDeviceGroup}
                    onDeletion={() => setOpenDeleteDeviceGroup(undefined)}
                    onClose={() => setOpenDeleteDeviceGroup(undefined)}
                />
            </Suspense>
        </HeadingLayout>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type DeviceGroupsOverviewSearch = {
    /** The search term that should be used */
    search: string;
    /** The current page number we're on */
    page: number;
};

export const Route = createFileRoute("/_admin/a/devicegroups/")({
    component: DeviceGroupManagement,
    validateSearch: (search: Record<string, unknown>): DeviceGroupsOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return { page: page <= 0 ? 1 : page, search: (search.search as string) || "" };
    },
    loaderDeps: ({ search: { search, page } }) => ({ page, search }),
    loader: async ({ deps }) =>
        await ApiInternal.deviceGroups.getAll({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
            filter_name: deps.search,
        }),
});
